import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { expand, map, reduce } from 'rxjs/operators';
import { EMPTY } from 'rxjs';
import { Producto, TipoProducto } from './models';
import { environment } from '../../environments/environment';

interface ContentResponse<T> {
    data: T[];
}

export interface TeamMember {
    id: number;
    image: string | null;
    name: string;
    role: string;
    sort: number | null;
}

export interface Project {
    id: number;
    cover: string | null;
    title: string;
    description: string;
    link: string | null;
    sort: number | null;
}

export interface NewsItem {
    id: number;
    cover: string | null;
    link: string;
    title: string;
    fuente: string;
    fecha: string;
    sort: number | null;
}

export type FooterLinkIcon = 'facebook' | 'twitter' | 'linkedin' | 'instagram' | 'skype' | 'website';

export interface FooterLink {
    id: number;
    label: string;
    url: string;
    icon: FooterLinkIcon;
    sort: number | null;
}

@Injectable({ providedIn: 'root' })
export class ContentService {
    constructor(private http: HttpClient) {}

    getPublications(): Observable<Producto[]> {
        return this.getLegacyCollection<Producto>('producto', 'idProducto',
            'idProducto,idTipoProducto,titulo,autores,revista,anio,isbn,paginas,doi,pais,palabrasClave,estado');
    }

    getPublicationTypes(): Observable<TipoProducto[]> {
        return this.getLegacyCollection<TipoProducto>('tipoProducto', 'idTipoProducto',
            'idTipoProducto,descripcion,estado');
    }

    private getLegacyCollection<T>(collection: string, key: string, fields: string): Observable<T[]> {
        const pageSize = 100;
        const readPage = (page: number) => this.http.get<ContentResponse<T>>(
            `${environment.contentUrl}/items/${collection}`, {
                params: new HttpParams().set('fields', fields).set('filter[estado][_eq]', '1')
                    .set('sort', key).set('limit', String(pageSize)).set('page', String(page))
            });
        return readPage(1).pipe(
            expand((response, index) => response.data.length === pageSize ? readPage(index + 2) : EMPTY),
            map(response => response.data),
            reduce((items: T[], page: T[]) => items.concat(page), [] as T[])
        );
    }

    getTeam(): Observable<ContentResponse<TeamMember>> {
        return this.getCollection<TeamMember>('equipo', ['id', 'image', 'name', 'role', 'sort']);
    }

    getProjects(): Observable<ContentResponse<Project>> {
        return this.getCollection<Project>('proyectos', ['id', 'cover', 'title', 'description', 'link', 'sort']);
    }

    getNews(): Observable<ContentResponse<NewsItem>> {
        return this.getCollection<NewsItem>('noticias', ['id', 'cover', 'link', 'title', 'fuente', 'fecha', 'sort']);
    }

    getFooterLinks(): Observable<ContentResponse<FooterLink>> {
        return this.getCollection<FooterLink>('enlaces_footer', ['id', 'label', 'url', 'icon', 'sort']);
    }

    assetUrl(fileId: string | null): string | null {
        return fileId ? `${environment.contentUrl}/assets/${fileId}` : null;
    }

    private getCollection<T>(collection: string, fields: string[]): Observable<ContentResponse<T>> {
        const params = new HttpParams()
            .set('fields', fields.join(','))
            .set('filter[status][_eq]', 'published')
            .set('sort', 'sort,id');

        return this.http.get<ContentResponse<T>>(
            `${environment.contentUrl}/items/${collection}`,
            { params }
        );
    }
}
